import { useMemo } from "react";

// Third parties
import { useSelector } from "react-redux";
import identity from "lodash/identity";

// Evergiving
import {
  FormConfig,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";
import {
  NameKind,
  Donor,
  PostalAddressKind,
} from "@evergiving/common/dist/es2015/pledge";

// Hooks
import { useAppSelector } from "@/lib/hooks/redux";

// Redux
import { RootState, VersionedStringFormState } from "@/lib/redux/types";

// Misc
import { createDetailsPageErrorsSelector } from "@/lib/misc/validations";

export enum SubmissionState {
  Unsubmitted,
  Submitting,
  SubmissionError,
}

export interface PartialDonor extends Omit<Donor, "name"> {
  name: {
    kind: NameKind.FirstLast;
  };
}

interface EssentialDonorFields {
  firstName: string;
  lastName: string;
}

interface PartialDonorFields {
  firstName: string | undefined;
  lastName: string | undefined;
}

type CanSubmitDonorTypeGuard = (
  input: PartialDonorFields
) => input is EssentialDonorFields;

interface UseAfterAddressFieldCommonProps {
  formConfig: FormConfig;
}

interface UseAfterAddressFieldCommonReturn {
  canSubmitDonor: CanSubmitDonorTypeGuard;
  partialDonor: PartialDonor;
  donorFields: PartialDonorFields;
  errors: { [key: string]: boolean };
  freeFormAddressNowComplete: boolean | "always";
  anyFormErrors: boolean;
}

function useFreeFormAddressComplete(
  formConfig: FormConfig
): boolean | "always" {
  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const { address } = formConfig;
  let requiredFields: FreeFormAddressElementType[] = [];
  if (address) {
    const config =
      (currentCountryCode
        ? address.freeForm.byCountry[currentCountryCode]
        : undefined) ?? address.freeForm.defaultConfig; // FIXME
    requiredFields = Object.values(FreeFormAddressElementType).filter(
      (key) => config[key]?.requiredFieldErrorText
    );
  }

  const postalAddressPresent = useSelector((state: RootState): boolean =>
    requiredFields.every(
      (field: keyof VersionedStringFormState) => !!state[field]?.value
    )
  );

  if (requiredFields.length === 0) {
    return "always";
  }

  return postalAddressPresent;
}

export const useAfterAddressFieldCommon = ({
  formConfig,
}: UseAfterAddressFieldCommonProps): UseAfterAddressFieldCommonReturn => {
  const firstName = useSelector((state: RootState) => state.firstName.value);

  const lastName = useSelector((state: RootState) => state.lastName.value);

  const mobilePhoneNumber = useSelector(
    (state: RootState) => state.phoneNumber.value
  );
  const emailAddress = useSelector(
    (state: RootState) => state.emailAddress.value
  );
  const emailAppraisal = useAppSelector(
    (state: RootState) => state.emailAppraisal
  );
  const postalAddress = useSelector((state: RootState) => state.postalAddress);
  const phoneContactConsent = useAppSelector(
    (state: RootState) => state.phoneContactConsent
  );
  const smsContactConsent = useAppSelector(
    (state: RootState) => state.smsContactConsent
  );
  const emailContactConsent = useAppSelector(
    (state: RootState) => state.emailContactConsent
  );
  const postContactConsent = useAppSelector(
    (state: RootState) => state.postContactConsent
  );
  const dateOfBirth = useAppSelector(
    (state: RootState) => state.dateOfBirth.value
  );
  const gender = useAppSelector((state: RootState) => state.gender.value);
  const profession = useAppSelector(
    (state: RootState) => state.profession.value
  );
  const title = useAppSelector((state: RootState) => state.title.value);
  const belfioreCode = useAppSelector(
    (state: RootState) => state.belfioreCode.value
  );

  const donorFields = useMemo(
    () => ({
      firstName,
      lastName,
    }),
    [firstName, lastName]
  );

  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const partialDonor: PartialDonor = {
    name: {
      kind: NameKind.FirstLast,
    },
    mobilePhoneNumber,
    emailAddress,
    emailAppraisal,
    postalAddress:
      postalAddress ||
      (formConfig.country
        ? {
            kind: PostalAddressKind.FreeForm,
            element2: currentCountryCode ?? formConfig.defaultCountryCode,
            element3: undefined,
            element4: undefined,
            element5: undefined,
            element6: undefined,
            element7: undefined,
            element8: undefined,
            element9: undefined,
            element10: undefined,
            element11: undefined,
            element12: undefined,
            element13: undefined,
            element14: undefined,
            element15: undefined,
          }
        : undefined),
    contactConsent: {
      phone: phoneContactConsent,
      sms: smsContactConsent,
      email: emailContactConsent,
      post: postContactConsent,
    },
    dateOfBirth,
    gender,
    profession,
    title,
    belfioreCode,
  };

  const canSubmitDonor: CanSubmitDonorTypeGuard = (
    input: PartialDonorFields
  ): input is EssentialDonorFields =>
    input.firstName !== undefined && input.lastName !== undefined;

  const detailsPageErrorsSelector = useMemo(
    () => createDetailsPageErrorsSelector(formConfig),
    [formConfig]
  );

  const errorsFromValidation = useSelector(detailsPageErrorsSelector);
  // FIXME: are there other fields that need their error state to be merged?
  const isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel = useSelector(
    (state: RootState) => state.errors.phoneNumber
  );
  const errors = useMemo(
    () => ({
      ...errorsFromValidation,
      phoneNumber:
        isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel ||
        errorsFromValidation.phoneNumber,
    }),
    [errorsFromValidation, isPhoneNumberAlreadyInErrorFromParsingAtFieldLevel]
  );

  const freeFormAddressNowComplete = useFreeFormAddressComplete(formConfig);

  const anyFormErrors = Object.values(errors).some(identity);

  return {
    canSubmitDonor,
    partialDonor,
    donorFields,
    errors,
    freeFormAddressNowComplete,
    anyFormErrors,
  };
};
